/**
 * TEMU query-site-list 响应解析
 * 文档：POST /open-api/goods/query-site-list
 *
 * 自运营售价币种取自 sub_site_list[].currency（store_type=2），
 * 不能用商品发布规范 【currency】 冒充；半托管/全托管供货价仍走发布规范。
 */

const SITE_STATUS_LABELS = {
  0: '未启用',
  1: '启用',
};

const STORE_TYPE_LABELS = {
  1: '平台类型',
  2: '自营类型',
};

/** query-site-list.sub_site_list.store_type：1 平台类型 / 2 自营类型 */
export const STORE_TYPE_PLATFORM = 1;
export const STORE_TYPE_SELF = 2;

function formatSiteStatus(value) {
  if (value == null || value === '') return '—';
  const key = Number(value);
  return SITE_STATUS_LABELS[key] ?? String(value);
}

function formatStoreType(value) {
  if (value == null || value === '') return '—';
  const key = Number(value);
  return STORE_TYPE_LABELS[key] ?? String(value);
}

function firstText(...values) {
  for (const value of values) {
    if (value == null || value === '') continue;
    const text = String(value).trim();
    if (text) return text;
  }
  return '';
}

function pickSiteCurrencyRaw(site) {
  if (!site || typeof site !== 'object') return '';
  return firstText(site.currency, site.currency_code, site.currencyCode);
}

function formatCurrency(site) {
  const currency = pickSiteCurrencyRaw(site).toUpperCase();
  return currency || '—';
}

function pickStoreTypeRaw(site) {
  if (!site || typeof site !== 'object') return null;
  const value = site.store_type ?? site.storeType;
  if (value == null || value === '') return null;
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
}

function pickSiteStatusRaw(site) {
  if (!site || typeof site !== 'object') return site;
  return site.site_status ?? site.siteStatus;
}

function pickSymbol(site, snake, camel) {
  if (!site || typeof site !== 'object') return '';
  return firstText(site[snake], site[camel]);
}

/**
 * 解开 query-site-list 各层包装，得到 main_site 数组。
 * 兼容：OpenAPI {info.data}、入库快照 {data}、SiteListView {siteList}、裸数组。
 */
function normalizeSiteListPayload(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw !== 'object') return [];
  if (Array.isArray(raw.data)) return raw.data;
  if (raw.info != null) {
    const nested = normalizeSiteListPayload(raw.info);
    if (nested.length) return nested;
  }
  if (raw.siteList != null && raw.siteList !== raw) {
    const nested = normalizeSiteListPayload(raw.siteList);
    if (nested.length) return nested;
  }
  return [];
}

export function flattenSiteRows(raw) {
  const mainSites = normalizeSiteListPayload(raw);
  const rows = [];

  // Temu 仓同步写入的扁平站点：[{ siteId, siteName }]
  const looksFlatTemu = mainSites.length > 0
    && mainSites.every((site) => site
      && typeof site === 'object'
      && !Array.isArray(site.sub_site_list)
      && !Array.isArray(site.subSiteList)
      && (site.siteId != null || site.site_id != null || site.siteName || site.site_name));

  if (looksFlatTemu) {
    mainSites.forEach((site, index) => {
      const siteId = firstText(site?.siteId, site?.site_id) || String(index);
      const siteName = firstText(site?.siteName, site?.site_name) || '—';
      rows.push({
        key: `temu-site-${siteId}`,
        mainSiteCode: 'temu',
        mainSiteName: 'TEMU',
        siteName,
        siteAbbr: siteId,
        siteStatusRaw: 1,
        siteStatus: formatSiteStatus(1),
        storeTypeRaw: null,
        storeType: '—',
        currency: '—',
        currencyCode: '',
        symbolLeft: '',
        symbolRight: '',
      });
    });
    return rows;
  }

  mainSites.forEach((mainSite, mainIndex) => {
    const mainSiteCode = firstText(mainSite?.main_site, mainSite?.mainSite) || 'temu';
    const mainSiteName = firstText(mainSite?.main_site_name, mainSite?.mainSiteName, mainSite?.main_site);
    const subSites = Array.isArray(mainSite?.sub_site_list)
      ? mainSite.sub_site_list
      : (Array.isArray(mainSite?.subSiteList) ? mainSite.subSiteList : []);
    subSites.forEach((site, subIndex) => {
      const siteAbbr = firstText(site?.site_abbr, site?.siteAbbr) || '—';
      const siteName = firstText(site?.site_name, site?.siteName) || '—';
      const siteStatusRaw = pickSiteStatusRaw(site);
      const storeTypeRaw = pickStoreTypeRaw(site);
      rows.push({
        key: `${mainIndex}-${subIndex}-${siteAbbr !== '—' ? siteAbbr : siteName || subIndex}`,
        mainSiteCode,
        mainSiteName,
        siteName,
        siteAbbr,
        siteStatusRaw,
        siteStatus: formatSiteStatus(siteStatusRaw),
        storeTypeRaw,
        storeType: formatStoreType(storeTypeRaw),
        currency: formatCurrency(site),
        // 发品 price_info_list.currency / 改价 currencyCode 需 ISO 大写（如 USD）
        currencyCode: pickSiteCurrencyRaw(site).toUpperCase(),
        symbolLeft: pickSymbol(site, 'symbol_left', 'symbolLeft'),
        symbolRight: pickSymbol(site, 'symbol_right', 'symbolRight'),
      });
    });
  });
  return rows;
}

/** 启用中的可上架站点 */
export function listEnabledSites(raw) {
  return flattenSiteRows(raw).filter((row) => Number(row.siteStatusRaw) === 1 && row.siteAbbr && row.siteAbbr !== '—');
}

/**
 * 发品用启用站点。
 * 若有 store_type=2（站点「自营类型」）优先用它们；
 * 没有则退回全部启用站点——商家「自运营」店铺的可售站常见 store_type=1（平台类型），
 * 这与 IPE 店铺类型不是同一字段，不能只认 2。
 */
export function listSelfOperatedSites(raw) {
  const enabled = listEnabledSites(raw);
  const selfSites = enabled.filter((row) => row.storeTypeRaw === STORE_TYPE_SELF);
  if (selfSites.length) return selfSites;
  return enabled;
}

/**
 * 自运营站点缩写 → ISO 币种。仅含有币种的站点。
 * @returns {Record<string, string>}
 */
export function extractSiteCurrencyMap(raw) {
  const map = {};
  listSelfOperatedSites(raw).forEach((row) => {
    const abbr = String(row.siteAbbr || '').trim();
    const code = String(row.currencyCode || '').trim().toUpperCase();
    if (!abbr || abbr === '—' || !code || map[abbr]) return;
    map[abbr] = code;
  });
  return map;
}

/**
 * 自运营可用币种列表（去重），形状与 pickPublishCurrency 兼容。
 * @returns {{ code: string, name: string, symbolLeft: string, symbolRight: string }[]}
 */
export function extractSiteListCurrencies(raw) {
  const out = [];
  const seen = new Set();
  listSelfOperatedSites(raw).forEach((row) => {
    const code = String(row.currencyCode || '').trim().toUpperCase();
    if (!code || seen.has(code)) return;
    seen.add(code);
    out.push({
      code,
      name: code,
      symbolLeft: String(row.symbolLeft || ''),
      symbolRight: String(row.symbolRight || ''),
    });
  });
  return out;
}

/**
 * 把现成 map 或 query-site-list 快照收成 siteAbbr → ISO 币种。
 * 优先用 siteCurrencyMap；为空再解析 siteList。
 * @returns {Record<string, string>}
 */
export function coerceSiteCurrencyMap({ siteCurrencyMap, siteList } = {}) {
  const fromMap = {};
  if (siteCurrencyMap && typeof siteCurrencyMap === 'object' && !Array.isArray(siteCurrencyMap)) {
    Object.entries(siteCurrencyMap).forEach(([site, code]) => {
      const abbr = String(site || '').trim();
      const currency = String(code || '').trim().toUpperCase();
      if (abbr && currency) fromMap[abbr] = currency;
    });
  }
  if (Object.keys(fromMap).length) return fromMap;
  return extractSiteCurrencyMap(siteList);
}

/**
 * 为模板 / 成品发布选默认币种。
 * 优先级：preferred（须在可用列表）→ 已选站点币种 → 列表第一项。
 */
export function pickSiteListCurrency(raw, { preferred = '', selectedSites = [] } = {}) {
  const currencies = extractSiteListCurrencies(raw);
  if (!currencies.length) return '';
  const want = String(preferred || '').trim().toUpperCase();
  if (want && currencies.some((row) => row.code === want)) return want;

  const map = extractSiteCurrencyMap(raw);
  const selected = (Array.isArray(selectedSites) ? selectedSites : [])
    .map((site) => String(site || '').trim())
    .filter(Boolean);
  for (const site of selected) {
    if (map[site]) return map[site];
  }
  return currencies[0].code || '';
}

export function countSiteRows(raw) {
  return flattenSiteRows(raw).length;
}

/**
 * siteAbbr / 短码 → 子站点全名（如 temu-us → TEMU美国站）
 */
export function buildSiteNameByAbbrMap(raw) {
  const map = new Map();
  for (const row of flattenSiteRows(raw)) {
    const abbr = String(row.siteAbbr || '').trim();
    const name = String(row.siteName || '').trim();
    if (!abbr || abbr === '—' || !name || name === '—') continue;
    const key = abbr.toLowerCase();
    map.set(key, name);
    const short = key.replace(/^temu-/, '');
    if (short && short !== key) {
      map.set(short, name);
    }
  }
  return map;
}

/** 无店铺站点缓存时的兜底：TEMU美国站 */
const FALLBACK_SUB_SITE_NAMES = {
  us: 'TEMU美国站',
  uk: 'TEMU英国站',
  fr: 'TEMU法国站',
  de: 'TEMU德国站',
  es: 'TEMU西班牙站',
  it: 'TEMU意大利站',
  nl: 'TEMU荷兰站',
  pl: 'TEMU波兰站',
  se: 'TEMU瑞典站',
  pt: 'TEMU葡萄牙站',
  eu: 'TEMU欧洲站',
  mx: 'TEMU墨西哥站',
  br: 'TEMU巴西站',
  au: 'TEMU澳大利亚站',
  ca: 'TEMU加拿大站',
};

/**
 * @param {string} siteAbbr 如 temu-us / us
 * @param {Map<string, string>|null|undefined} siteNameByAbbr
 */
export function resolveSubSiteDisplayName(siteAbbr, siteNameByAbbr) {
  const raw = String(siteAbbr || '').trim();
  if (!raw) return '';
  const key = raw.toLowerCase();
  const short = key.replace(/^temu-/, '');
  const fromMap = siteNameByAbbr?.get(key)
    || siteNameByAbbr?.get(short)
    || (short !== key ? siteNameByAbbr?.get(`temu-${short}`) : null);
  if (fromMap) return fromMap;
  return FALLBACK_SUB_SITE_NAMES[short] || raw;
}

export function formatSiteListSyncedAt(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString('zh-CN', { hour12: false });
}
